import { useState } from "react";
import { router, useForm } from "@inertiajs/react";
import {
  ArrowTopRightOnSquareIcon,
  KeyIcon,
  PlusIcon,
  TrashIcon,
  CheckCircleIcon,
} from "@heroicons/react/24/outline";
import { Button } from "@components/ui/button";
import { Input } from "@components/ui/input";
import { Copyable } from "@components/ui/copyable";

export interface ApiToken {
  id: number;
  name: string;
  last_used_at: string | null;
  created_at: string;
  expires_at: string | null;
  token_preview: string;
}

export interface ZapierWebhook {
  id: number;
  name: string | null;
  target_url: string;
  target_host: string;
  last_delivered_at: string | null;
  created_at: string;
}

export interface RevealedToken {
  id: number;
  name: string;
  token: string;
}

interface ZapierPanelProps {
  apiTokens: ApiToken[];
  zapierWebhooks: ZapierWebhook[];
  zapierInviteUrl: string | null;
  revealedToken: RevealedToken | null;
}

function formatRelative(iso: string | null) {
  if (!iso) return "Never";
  const date = new Date(iso);
  return date.toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" });
}

export function ZapierPanel({ apiTokens, zapierWebhooks, zapierInviteUrl, revealedToken }: ZapierPanelProps) {
  const [showNewTokenForm, setShowNewTokenForm] = useState(apiTokens.length === 0);

  const { data, setData, post, processing } = useForm({ api_token: { name: "" } });

  const handleCreate = () => {
    post("/integrations/api_tokens", {
      preserveScroll: true,
      onSuccess: () => {
        setShowNewTokenForm(false);
        setData({ api_token: { name: "" } });
      },
    });
  };

  const handleRevoke = (id: number) => {
    if (!confirm("Revoke this API key? Any connected Zaps will stop receiving events.")) return;
    router.delete(`/integrations/api_tokens/${id}`, { preserveScroll: true });
  };

  const openZapier = () => {
    if (!zapierInviteUrl) return;
    window.open(zapierInviteUrl, "_blank", "noopener,noreferrer");
  };

  return (
    <div className="space-y-6">
      {/* Step 1: Install */}
      <section className="rounded-2xl border border-[#E5E5E4] bg-white p-5">
        <div className="flex items-start gap-4">
          <StepNumber n={1} />
          <div className="flex-1">
            <h3 className="font-['Plus_Jakarta_Sans'] text-base font-semibold text-[#2E3238]">
              Install Launch10 in your Zapier account
            </h3>
            <p className="mt-1 font-['Plus_Jakarta_Sans'] text-sm text-[#74767A]">
              Opens Zapier in a new tab. You'll accept an invite that adds Launch10 to your Zapier
              account. It's free.
            </p>
            <Button
              onClick={openZapier}
              disabled={!zapierInviteUrl}
              size="sm"
              className="mt-3"
            >
              <ArrowTopRightOnSquareIcon className="h-4 w-4" />
              {zapierInviteUrl ? "Open Zapier" : "Coming soon"}
            </Button>
            {!zapierInviteUrl && (
              <p className="mt-2 font-['Plus_Jakarta_Sans'] text-xs text-[#96989B]">
                The Zapier integration will be available shortly.
              </p>
            )}
          </div>
        </div>
      </section>

      {/* Step 2: API Key */}
      <section className="rounded-2xl border border-[#E5E5E4] bg-white p-5">
        <div className="flex items-start gap-4">
          <StepNumber n={2} />
          <div className="flex-1">
            <h3 className="font-['Plus_Jakarta_Sans'] text-base font-semibold text-[#2E3238]">
              Generate your API key
            </h3>
            <p className="mt-1 font-['Plus_Jakarta_Sans'] text-sm text-[#74767A]">
              Copy this into Zapier when it asks for your Launch10 API key.
            </p>

            {revealedToken && (
              <div className="mt-4 rounded-xl border border-[#BDE8D2] bg-[#F0FAF5] p-4">
                <div className="flex items-center gap-2">
                  <CheckCircleIcon className="h-5 w-5 text-[#2E9E72]" />
                  <p className="font-['Plus_Jakarta_Sans'] text-sm font-semibold text-[#2E3238]">
                    New key created — copy it now
                  </p>
                </div>
                <p className="mt-1 font-['Plus_Jakarta_Sans'] text-xs text-[#74767A]">
                  You won't be able to see it again after you leave this page.
                </p>
                <div className="mt-3 flex items-center gap-2 rounded-lg border border-[#D3D2D0] bg-white px-3 py-2">
                  <code className="flex-1 truncate font-mono text-xs text-[#2E3238]">
                    {revealedToken.token}
                  </code>
                  <Copyable text={revealedToken.token}>
                    <Copyable.Trigger />
                  </Copyable>
                </div>
              </div>
            )}

            {/* Existing tokens */}
            {apiTokens.length > 0 && (
              <div className="mt-4 space-y-2">
                {apiTokens.map((token) => (
                  <div
                    key={token.id}
                    className="flex items-center justify-between rounded-lg border border-[#E5E5E4] bg-[#FAFAF9] px-4 py-3"
                  >
                    <div className="flex items-center gap-3">
                      <KeyIcon className="h-4 w-4 text-[#96989B]" />
                      <div>
                        <p className="font-['Plus_Jakarta_Sans'] text-sm font-medium text-[#2E3238]">
                          {token.name}
                        </p>
                        <p className="font-['Plus_Jakarta_Sans'] text-xs text-[#96989B]">
                          {token.token_preview} · Last used {formatRelative(token.last_used_at)}
                        </p>
                      </div>
                    </div>
                    <button
                      type="button"
                      onClick={() => handleRevoke(token.id)}
                      className="text-[#96989B] hover:text-[#D14F34]"
                      aria-label="Revoke"
                    >
                      <TrashIcon className="h-4 w-4" />
                    </button>
                  </div>
                ))}
              </div>
            )}

            {/* New key form */}
            {showNewTokenForm ? (
              <div className="mt-4 space-y-3">
                <Input
                  type="text"
                  value={data.api_token.name}
                  onChange={(e) => setData("api_token", { name: e.target.value })}
                  placeholder="Name this key (e.g. Zapier)"
                  className="h-10"
                />
                <div className="flex gap-2">
                  <Button size="sm" onClick={handleCreate} disabled={processing}>
                    {processing ? "Generating…" : "Generate key"}
                  </Button>
                  <Button
                    size="sm"
                    variant="ghost"
                    onClick={() => setShowNewTokenForm(false)}
                    disabled={processing}
                  >
                    Cancel
                  </Button>
                </div>
              </div>
            ) : (
              <Button
                variant="outline"
                size="sm"
                onClick={() => setShowNewTokenForm(true)}
                className="mt-4"
              >
                <PlusIcon className="h-4 w-4" />
                New API key
              </Button>
            )}
          </div>
        </div>
      </section>

      {/* Step 3: Build your Zap */}
      <section className="rounded-2xl border border-[#E5E5E4] bg-white p-5">
        <div className="flex items-start gap-4">
          <StepNumber n={3} />
          <div className="flex-1">
            <h3 className="font-['Plus_Jakarta_Sans'] text-base font-semibold text-[#2E3238]">
              Build your first Zap
            </h3>
            <p className="mt-1 font-['Plus_Jakarta_Sans'] text-sm text-[#74767A]">
              In Zapier, pick Launch10 as the trigger, choose “New Lead,” paste your API key, and
              connect any of 6,000+ apps as the action.
            </p>
            <Button
              size="sm"
              variant="outline"
              className="mt-3"
              onClick={() => window.open("https://zapier.com/app/zaps/new", "_blank", "noopener,noreferrer")}
            >
              <ArrowTopRightOnSquareIcon className="h-4 w-4" />
              Open Zap editor
            </Button>
          </div>
        </div>
      </section>

      {/* Connected Zaps */}
      {zapierWebhooks.length > 0 && (
        <section className="rounded-2xl border border-[#E5E5E4] bg-white p-5">
          <h3 className="font-['Plus_Jakarta_Sans'] text-base font-semibold text-[#2E3238]">
            Connected Zaps
          </h3>
          <p className="mt-1 font-['Plus_Jakarta_Sans'] text-sm text-[#74767A]">
            Active Zaps subscribed to your lead events.
          </p>
          <div className="mt-4 space-y-2">
            {zapierWebhooks.map((wh) => (
              <div
                key={wh.id}
                className="flex items-center justify-between rounded-lg border border-[#E5E5E4] bg-[#FAFAF9] px-4 py-3"
              >
                <div>
                  <p className="font-['Plus_Jakarta_Sans'] text-sm font-medium text-[#2E3238]">
                    {wh.target_host}
                  </p>
                  <p className="font-['Plus_Jakarta_Sans'] text-xs text-[#96989B]">
                    Last delivery {formatRelative(wh.last_delivered_at)}
                  </p>
                </div>
                <span className="inline-flex h-2 w-2 rounded-full bg-[#2E9E72]" aria-label="Active" />
              </div>
            ))}
          </div>
        </section>
      )}
    </div>
  );
}

function StepNumber({ n }: { n: number }) {
  return (
    <div className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-[#5867C4] font-['Plus_Jakarta_Sans'] text-sm font-semibold text-white">
      {n}
    </div>
  );
}
